import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { 
  Box, 
  Checkbox, 
  FormControl, 
  FormControlLabel, 
  FormGroup, 
  FormLabel, 
  Button,
  CircularProgress, 
  Alert,
  Typography,
} from '@mui/material';
import { Pie, Line } from 'react-chartjs-2';
import { 
  Chart as ChartJS, 
  ArcElement, 
  Tooltip, 
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Filler
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import IncomePlanWrapper from '../components/income-plan-wrapper';
import { generatePDF } from '../components/pdf/ReportPDF';
import tabs from '../lib/client-tabs';
import { apiFetch } from '../lib/fetch';
import useUser from '../lib/useUser';
import { colors } from '../lib/styles';
import { generateReportsClient } from '../lib/client';
import { getReportOptions, getReportError, toggleReportOption, resetReportOptions, setReportError } from '../state/reports';
import { Form } from 'formik';

ChartJS.register(
  ArcElement, 
  Tooltip, 
  Legend, 
  ChartDataLabels,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Filler
);

const styles = {
  mainBody: {
    padding: '20px',
  },
  errTxt: {
    color: 'red',
    marginBottom: '15px',
  },
  tdCurrency: {
    textAlign: 'right',
    width: '100px',
    margin: '5px',
  },
  buttonContainer: {
    marginTop: '20px',
    display: 'flex',
    gap: '10px',
  },
  chartContainer: {
    marginTop: '30px',
    width: '100%',
  },
  previewContainer: {
    marginTop: '30px',
    padding: '15px',
    backgroundColor: '#f5f5f5',
    borderRadius: '5px',
  },
  previewTitle: {
    fontSize: '18px',
    fontWeight: 'bold',
    marginBottom: '10px',
    color: '#1d428a',
  },
  loader: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: '30px',
  },
  sectionTitle: {
    fontSize: '20px',
    fontWeight: 'bold',
    marginTop: '30px',
    marginBottom: '15px',
    color: '#1d428a',
  }
}

function ClientReports() {
  const auth = useUser();
  const { id } = useParams();
  const dispatch = useDispatch();
  const reportOptions = useSelector(getReportOptions);
  const error = useSelector(getReportError);
  const [client, setClient] = useState(null);
  const [err, setErr] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [chartImages, setChartImages] = useState({});

  // Refs for Chart.js instances
  const incomePlanChartRef = useRef(null);
  const assetsByYearChartRef = useRef(null);
  const incomeByYearChartRef = useRef(null);
  const defaultPlanChartRef = useRef(null);

useEffect(() => {
  if (!auth?.id) return;
  apiFetch(`/users/${auth.id}/ip_households/${id}`)
    .then((res) => {
      return generateReportsClient(res);
    })
    .then((res) => {
      setClient(res);
    })
    .catch((e) => {
      let err = e.toString();
      setErr(err);
    });
}, [auth?.id, id]);

  const handleChange = (event) => {
    dispatch(toggleReportOption(event.target.name));
  };

  const handleReset = () => {
    dispatch(resetReportOptions());
  };

  // Handle chart capture from onComplete callbacks
  const handleChartCapture = useCallback((chartKey, base64Image) => {
    setChartImages(prev => {
      const updated = { ...prev, [chartKey]: base64Image };
      return updated;
    });
  }, []);

  // Reset chart images when report options change
  useEffect(() => {
    setChartImages({});
  }, [reportOptions]);

  // Capture charts after client data loads
  useEffect(() => {
    if (!client) return;

    const captureCharts = () => {
      const captured = {};

      if (reportOptions.redesigned_income_plan && incomePlanChartRef.current) {
        const canvas = incomePlanChartRef.current.querySelector('canvas');
        if (canvas) {
          try {
            captured['income-plan-chart'] = canvas.toDataURL('image/png');
          } catch (err) {
            console.error('Error capturing income-plan-chart:', err);
          }
        }
      }

      if (reportOptions.assets_by_year && assetsByYearChartRef.current) {
        const canvas = assetsByYearChartRef.current.querySelector('canvas');
        if (canvas) {
          try {
            captured['assets-by-year-chart'] = canvas.toDataURL('image/png');
          } catch (err) {
            console.error('Error capturing assets-by-year-chart:', err);
          }
        }
      }

      if (reportOptions.income_by_year && incomeByYearChartRef.current) {
        const canvas = incomeByYearChartRef.current.querySelector('canvas');
        if (canvas) {
          try {
            captured['income-by-year-chart'] = canvas.toDataURL('image/png');
          } catch (err) {
            console.error('Error capturing income-by-year-chart:', err);
          }
        }
      }

      if (reportOptions.default_retirement_plan && defaultPlanChartRef.current) {
        const canvas = defaultPlanChartRef.current.querySelector('canvas');
        if (canvas) {
          try {
            captured['default-plan-chart'] = canvas.toDataURL('image/png');
          } catch (err) {
            console.error('Error capturing default-plan-chart:', err);
          }
        }
      }

      if (Object.keys(captured).length > 0) {
        setChartImages(prev => ({ ...prev, ...captured }));
      }
    };

    // Give charts time to render
    const timer = setTimeout(captureCharts, 1000);
    return () => clearTimeout(timer);
  }, [client, reportOptions]);

  // Check if all required charts are captured
  const areAllChartsReady = useMemo(() => {
    const requiredCharts = [];
    if (reportOptions.default_retirement_plan) requiredCharts.push('default-plan-chart');
    if (reportOptions.redesigned_income_plan) requiredCharts.push('income-plan-chart');
    if (reportOptions.assets_by_year) requiredCharts.push('assets-by-year-chart');
    if (reportOptions.income_by_year) requiredCharts.push('income-by-year-chart');

    return requiredCharts.every(chart => chartImages[chart]);
  }, [reportOptions, chartImages]);


    // Memoize chart data calculations
  const assetsProjectionData = useMemo(() => {
    return client?.assetsByYearData || [];
  }, [client]);

  const incomeProjectionData = useMemo(() => {
    return client?.incomeByYearData || [];
  }, [client]);

  const handleGenerateReport = async () => {
    if (!client) return;

    try {
      setIsLoading(true);
      setLoadingMessage('Generating PDF...');

      await generatePDF(client, reportOptions, chartImages);

    } catch (error) {
      console.error('Error generating report:', error);
      dispatch(setReportError(`Error generating report: ${error.message}`));
    } finally {
      setIsLoading(false);
      setLoadingMessage('');
    }
  };

  return (
    <IncomePlanWrapper handleSubmit={() => {}}>
      <Box style={styles.mainBody}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error.toString()}</Alert>}
        
        <Typography variant="h5" component="h1" sx={{ mb: 3, color: '#1d428a', fontWeight: 'bold' }}>
          Client Financial Report Generator
        </Typography>
        
        {isLoading ? (
          <Box style={styles.loader}>
            <CircularProgress size={40} sx={{ color: '#1d428a' }} />
            <Box ml={2}>{loadingMessage}</Box>
          </Box>
        ) : (
          <>
            <FormControl component="fieldset" fullWidth>
              <FormLabel component="legend">Select sections to include in report</FormLabel>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={reportOptions.cover_page || false}
                      onChange={handleChange}
                      name="cover_page"
                      color="primary"
                    /> 
                  }
                  label="Cover Page"
                />
                {
                  Object.keys(reportOptions).map((key) => {
                    if (tabs.find(tab => tab.name === key)) {
                      return (
                        <FormControlLabel
                          key={key}
                          control={
                            <Checkbox
                              checked={reportOptions[key] || false}
                              onChange={handleChange}
                              name={key}
                              color="primary"
                            />
                          }
                          label={key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        />
                      );
                    }
                    return null;
                  })
                }
              </FormGroup>

              <Box style={styles.buttonContainer}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleGenerateReport}
                  disabled={isLoading || !client || !areAllChartsReady}
                  sx={{
                    backgroundColor: '#1d428a',
                    '&:hover': {
                      backgroundColor: '#15316a',
                    }
                  }}
                >
                  Generate Report
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleReset}
                  disabled={isLoading}
                  sx={{
                    borderColor: '#1d428a',
                    color: '#1d428a',
                    '&:hover': {
                      borderColor: '#15316a',
                    }
                  }}
                >
                  Reset Selections
                </Button>
              </Box>

              {/* Loading Status */}
              {isLoading && loadingMessage && (
                <Box sx={{ mt: 2, p: 2, backgroundColor: '#f0f4f8', borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ color: '#1d428a', fontWeight: 'bold' }}>
                    {loadingMessage}
                  </Typography>
                </Box>
              )}

            </FormControl>
            
            {client && (
              <>
                {/* Redesigned Income Plan Chart */}
                {reportOptions.redesigned_income_plan && (
                  <Box
                    ref={incomePlanChartRef}
                    sx={{
                      position: 'absolute',
                      left: '-9999px',
                      top: '-9999px',
                      p: 1,
                      textAlign: 'center',
                      height: '600px',
                      width: '600px',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      margin: '0 auto',
                      backgroundColor: colors.white,
                      borderRadius: '8px',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    }}
                  >
                    <Pie
                      data={{
                        labels: [
                          'ECP',
                          'Income',
                          'Growth'
                        ],
                        datasets: [
                          {
                            data: [
                              client.additionalNeedsPercentage,
                              client.pieChartIncomePercentage,
                              client.pieChartGrowthPercentage
                            ],
                            backgroundColor: [
                              colors.primaryLighter,
                              colors.primaryColor,
                              colors.primaryDarker,
                            ],
                            borderColor: [
                              colors.primaryLighter,
                              colors.primaryColor,
                              colors.primaryDarker,
                            ],
                          },
                        ],
                      }}
                      options={{
                        maintainAspectRatio: false,
                        plugins: {
                          datalabels: {
                            formatter: (value) => value + '%',
                            color: '#fff',
                            font: {
                              weight: 'bold',
                              size: 36
                            }
                          },
                          tooltip: {
                            enabled: false,
                          },
                          legend: {
                            display: true,
                            position: 'bottom',
                            labels: {
                              font: {
                                size: 36
                              }
                            },

                          },
                          title: {
                            display: false,
                          },
                        },
                        animation: {
                          duration: 500,
                        },
                      }}
                    />
                  </Box>
                )}

                {/* Assets By Year Chart */}
                {reportOptions.assets_by_year && assetsProjectionData && (
                  <Box
                    ref={assetsByYearChartRef}
                    sx={{
                      position: 'absolute',
                      left: '-9999px',
                      top: '-9999px',
                      p: 1,
                      textAlign: 'center',
                      height: '400px',
                      width: '800px',
                      margin: '20px auto',
                      backgroundColor: colors.white,
                      borderRadius: '8px',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    }}
                  >
                    <Line
                      data={{
                        labels: assetsProjectionData.map(row => row.year),
                        datasets: [
                          {
                            label: 'Total Projected Assets',
                            data: assetsProjectionData.map(row => Number(row.yearTotalAssets || 0)),
                            borderColor: colors.primaryColor,
                            backgroundColor: 'rgba(142, 160, 196, 0.4)',
                            fill: true,
                            tension: 0.4,
                            pointRadius: 3,
                            pointHoverRadius: 6,
                            datalabels: {
                              display: false,
                            },
                          },
                        ],
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          legend: {
                            display: false,
                          },
                          title: {
                            display: false,
                          },
                          tooltip: {
                            enabled: false,
                          },
                        },
                        animation: {
                          duration: 500,
                        },
                        scales: {
                          y: {
                            ticks: {
                              callback: (value) => `$${value.toLocaleString()}`,
                              color: colors.black,
                            },
                          },
                          x: {
                            ticks: {
                              color: colors.black,
                            },
                          },
                        },
                      }}
                    />
                  </Box>
                )}

                {/* Income By Year Section */}
                {reportOptions.income_by_year && incomeProjectionData && (
                  <Box
                    ref={incomeByYearChartRef}
                    sx={{
                      position: 'absolute',
                      left: '-9999px',
                      top: '-9999px',
                      p: 1,
                      textAlign: 'center',
                      height: '400px',
                      width: '800px',
                      margin: '0 auto',
                      backgroundColor: colors.white,
                      borderRadius: '8px',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    }}
                  >
                    <Line
                      data={{
                        labels: incomeProjectionData.map(row => row.year),
                        datasets: [
                          {
                            label: 'Total Projected Income',
                            data: incomeProjectionData.map(row => Number(row.totalPlanIncomeAfterTax).toFixed(2)),
                            borderColor: colors.primaryColor,
                            backgroundColor: 'rgba(142, 160, 196, 0.4)',
                            fill: true,
                            tension: 0.4,
                            pointRadius: 3,
                            pointHoverRadius: 6,
                            datalabels: {
                              display: false,
                            }
                          },
                        ],
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          legend: {
                            display: false,
                          },
                          title: {
                            display: false,
                          },
                          tooltip: {
                          enabled: false,
                        },
                        },
                        animation: {
                          duration: 500,
                        },
                        scales: {
                          y: {
                            ticks: {
                              callback: (value) => `$${value.toLocaleString()}`,
                              color: colors.black,
                            },
                          },
                          x: {
                            ticks: {
                              color: colors.black,
                            },
                          },
                        },
                      }}
                    />
                  </Box>
                )}

                {/* Default Plan Chart - Sequence of Returns Risk */}
                {reportOptions.default_retirement_plan && (
                  <Box
                    ref={defaultPlanChartRef}
                    sx={{
                      position: 'absolute',
                      left: '-9999px',
                      top: '-9999px',
                      p: 1,
                      textAlign: 'center',
                      height: '300px',
                      width: '600px',
                      margin: '0 auto',
                      backgroundColor: colors.white,
                      borderRadius: '8px',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    }}
                  >
                    <Line
                      data={{
                        labels: Array.from({ length: 41 }, (_, i) => i.toString()),
                        datasets: [
                          {
                            label: 'Balance - Scenario 1',
                            data: (() => {
                              const totalNQAssets = client.assets?.filter((a) => a.type === 'NON_QUALIFIED')
                                .reduce((acc, a) => acc + (Number(a.value) || 0), 0) || 0;
                              const currentInvestableAssets = client.include_nq_assets !== false
                                ? client.totalRetAssets
                                : client.totalRetAssets - totalNQAssets;
                              const annualWithdrawal = currentInvestableAssets * ((Number(client.asset_withdrawal_rate) || 4) / 100);
                              const inflationRate = Number(client.inflation_rate) || 2.5;
                              const pattern1 = [25, 10, 5, -15];
                              const data = [];
                              let balance = currentInvestableAssets;
                              for (let year = 0; year <= 40; year++) {
                                data.push(Math.max(0, balance));
                                if (year < 40 && balance > 0) {
                                  const adjustedWithdrawal = annualWithdrawal * Math.pow(1 + inflationRate / 100, year);
                                  balance = balance * (1 + pattern1[year % 4] / 100) - adjustedWithdrawal;
                                  balance = Math.max(0, balance);
                                }
                              }
                              return data;
                            })(),
                            borderColor: colors.primaryColor,
                            backgroundColor: 'transparent',
                            borderWidth: 3,
                            tension: 0.1,
                            datalabels: {
                              display: false,
                            }
                          },
                          {
                            label: 'Balance - Scenario 2',
                            data: (() => {
                              const totalNQAssets = client.assets?.filter((a) => a.type === 'NON_QUALIFIED')
                                .reduce((acc, a) => acc + (Number(a.value) || 0), 0) || 0;
                              const currentInvestableAssets = client.include_nq_assets !== false
                                ? client.totalRetAssets
                                : client.totalRetAssets - totalNQAssets;
                              const annualWithdrawal = currentInvestableAssets * ((Number(client.asset_withdrawal_rate) || 4) / 100);
                              const inflationRate = Number(client.inflation_rate) || 2.5;
                              const pattern2 = [-15, 5, 10, 25];
                              const data = [];
                              let balance = currentInvestableAssets;
                              for (let year = 0; year <= 40; year++) {
                                data.push(Math.max(0, balance));
                                if (year < 40 && balance > 0) {
                                  const adjustedWithdrawal = annualWithdrawal * Math.pow(1 + inflationRate / 100, year);
                                  balance = balance * (1 + pattern2[year % 4] / 100) - adjustedWithdrawal;
                                  balance = Math.max(0, balance);
                                }
                              }
                              return data;
                            })(),
                            borderColor: '#dc3545',
                            backgroundColor: 'transparent',
                            borderWidth: 3,
                            tension: 0.1,
                            datalabels: {
                              display: false,
                            }
                          },
                        ],
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          datalabels: {
                            display: false,
                          },
                          title: {
                            display: true,
                            text: 'Sequence of Returns Risk',
                            font: {
                              size: 18,
                              weight: 'bold'
                            },
                            color: colors.darkAccent,
                            padding: 20,
                          },
                          legend: {
                            position: 'top',
                            labels: {
                              boxWidth: 15,
                              padding: 15,
                              usePointStyle: true,
                              font: {
                                size: 14,
                              },
                            },
                          },
                          tooltip: {
                            enabled: false,
                          },
                        },
                        animation: {
                          duration: 500,
                        },
                        scales: {
                          y: {
                            title: {
                              display: true,
                              text: 'Balance',
                              font: {
                                size: 14,
                                weight: 'bold'
                              }
                            },
                            grid: {
                              color: '#e0e0e0',
                            },
                            ticks: {
                              callback: function(value) {
                                return '$' + value.toLocaleString();
                              }
                            }
                          },
                          x: {
                            title: {
                              display: true,
                              text: 'Year',
                              font: {
                                size: 14,
                                weight: 'bold'
                              }
                            },
                            grid: {
                              color: '#e0e0e0',
                            },
                            ticks: {
                              maxTicksLimit: 10,
                            }
                          }
                        },
                      }}
                    />
                  </Box>
                )}
              </>
            )}
          </>
        )}
      </Box>
    </IncomePlanWrapper>
  );
}

export default ClientReports;